import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, BarChart3, CheckCircle2, Sparkles, TrendingUp, TrendingDown, Download, Printer, Loader2, Briefcase, CreditCard, FileText, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Nota: Este preview es auto-contenido para visualizar la propuesta de look & feel
// Puedes copiar secciones a tu proyecto real (hero, features, CTA, footer, banner de cookies)

function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [consented, setConsented] = useState(false);

  useEffect(() => {
    const v = localStorage.getItem("analyticsConsent") === "true";
    setConsented(v);
    setVisible(!v);
  }, []);

  const accept = () => {
    localStorage.setItem("analyticsConsent", "true");
    setConsented(true);
    setVisible(false);
  };

  if (!visible) return null;
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[95%] md:w-[820px]">
      <Card className="shadow-2xl">
        <CardContent className="p-4 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="text-sm md:text-base">
            Usamos cookies analíticas opcionales para mejorar tu experiencia. Solo se activan si aceptas.
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="secondary" onClick={() => setVisible(false)}>Rechazar</Button>
            <Button onClick={accept}>Aceptar</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Badge({ children }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs md:text-sm bg-white/60 backdrop-blur shadow-sm">
      <Sparkles className="w-4 h-4" /> {children}
    </span>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="w-10 h-10 rounded-2xl bg-gray-100 flex items-center justify-center mb-2">
          <Icon className="w-5 h-5" />
        </div>
        <CardTitle className="text-base md:text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground">{desc}</CardContent>
    </Card>
  );
}

//
function BackgroundFX() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
      {/* Blur blobs */}
      <div className="absolute -top-32 -right-24 h-80 w-80 rounded-full bg-sky-300/30 blur-3xl" />
      <div className="absolute top-24 -left-24 h-72 w-72 rounded-full bg-blue-200/40 blur-2xl" />
      {/* Dotted grid */}
      <div
        className="absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage:
            "radial-gradient(rgba(14,165,233,.35) 1px, transparent 1px)",
          backgroundSize: "18px 18px",
          maskImage:
            "radial-gradient(ellipse at center, black 40%, transparent 70%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 40%, transparent 70%)",
        }}
      />
    </div>
  );
}

function BottomFX() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-[420px] -z-10 overflow-hidden">
      {/* Degradé suave hacia arriba */}
      <div className="absolute inset-0 bg-gradient-to-t from-sky-100/70 via-sky-50/50 to-transparent" />
      {/* Blobs suaves en esquinas inferiores */}
      <div className="absolute -bottom-20 -left-24 h-72 w-72 rounded-full bg-sky-300/30 blur-3xl" />
      <div className="absolute -bottom-28 -right-24 h-80 w-80 rounded-full bg-blue-200/30 blur-3xl" />
      {/* Trama sutil de puntos que se desvanece hacia arriba */}
      <div
        className="absolute inset-0 opacity-[0.12]"
        style={{
          backgroundImage: "radial-gradient(rgba(14,165,233,.3) 1px, transparent 1px)",
          backgroundSize: "18px 18px",
          maskImage: "linear-gradient(to top, black 20%, transparent 80%)",
          WebkitMaskImage: "linear-gradient(to top, black 20%, transparent 80%)",
        }}
      />
    </div>
  );
}

// Sección de testimonios eliminada según solicitud
//

export default function LandingPreview() {
  // Selección de empresa y análisis sintético
  const companies = [
    // Chicas
    { id: 1, nombre: "Panadería Ñuñoa SpA", segmento: 'chica' },
    { id: 2, nombre: "Servicios Patagona Ltda", segmento: 'chica' },
    { id: 3, nombre: "Taller Maipo EIRL", segmento: 'chica' },
    // Medianas
    { id: 4, nombre: "Logística Arauco EIRL", segmento: 'mediana' },
    { id: 5, nombre: "Retail BioBio Ltda", segmento: 'mediana' },
    { id: 6, nombre: "Tecnologías Valles SpA", segmento: 'mediana' },
    // Grandes
    { id: 7, nombre: "Industrias Maipo S.A.", segmento: 'grande' },
    { id: 8, nombre: "Transporte Austral S.A.", segmento: 'grande' },
    { id: 9, nombre: "Insumos Médicos Sur SpA", segmento: 'grande' },
  ];
  const [selectedCompany, setSelectedCompany] = useState(companies[0].id);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeSteps, setAnalyzeSteps] = useState([]);

  const [scrolled, setScrolled] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [result, setResult] = useState(null); // JSON de resultados (sintético)
  const [resultRaw, setResultRaw] = useState(""); // Texto crudo para panel
  const [resultInfo, setResultInfo] = useState(null);
  const fileInputRef = useRef(null);
  const [openReport, setOpenReport] = useState(false);
  const reportUrl = "/reportes/reporte-estandar.pdf"; // Siempre el mismo reporte
  const iframeRef = useRef(null);
  const [pdfOk, setPdfOk] = useState(true);
  const [openRegister, setOpenRegister] = useState(false);
  const [detailKey, setDetailKey] = useState(null); // 'rating' | 'ventas' | 'ingresos' | 'clientes' | 'evolucion'
  const [regNombre, setRegNombre] = useState("");
  const [regApellido, setRegApellido] = useState("");
  const [regTelefono, setRegTelefono] = useState("");
  const [regCorreo, setRegCorreo] = useState("");
  const [regErrors, setRegErrors] = useState({});
  const [goal, setGoal] = useState("");
  const [regLoading, setRegLoading] = useState(false);
  const [actionStep, setActionStep] = useState("company"); // company -> goal -> amount -> offer|plan
  const [actionCompanyId, setActionCompanyId] = useState(companies[0].id);
  const [actionResult, setActionResult] = useState(null);
  const [amountRange, setAmountRange] = useState("");
  const goalLabel = (g) => (
    g === 'capital' ? 'Capital de trabajo' :
    g === 'linea' ? 'Línea de crédito' :
    g === 'factoring' ? 'Factoring' :
    g === 'seguros' ? 'Seguros' : ''
  );
  const handlePrint = () => {
    try {
      const win = iframeRef.current?.contentWindow;
      win?.focus();
      win?.print();
    } catch (e) {
      window.open(reportUrl, "_blank");
    }
  };

  useEffect(() => {
    // Verificar disponibilidad del PDF al abrir el modal
    let cancelled = false;
    if (openReport) {
      fetch(reportUrl, { method: 'HEAD' })
        .then((r) => { if (!cancelled) setPdfOk(r.ok); })
        .catch(() => { if (!cancelled) setPdfOk(false); });
    }
    return () => { cancelled = true; };
  }, [openReport, reportUrl]);

  // Generador sintético con diferencia clara entre empresas (1=debil,2=media,3=solida por segmento)
  const makeSynthetic = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    const seg = company?.segmento || 'mediana';
    // Determinar tier dentro del segmento según posición (ids secuenciales por segmento)
    const segmentCompanies = companies.filter(c => c.segmento === seg);
    const indexInSegment = segmentCompanies.findIndex(c => c.id === companyId); // 0,1,2
    const tier = indexInSegment === 0 ? 'low' : indexInSegment === 1 ? 'mid' : 'high';

    // Rango mensual MM por segmento y tier
    const monthlyRanges = {
      chica: {
        low: [5, 9],
        mid: [11, 17],
        high: [20, 25]
      },
      mediana: {
        low: [40, 70],
        mid: [90, 130],
        high: [150, 180]
      },
      grande: {
        low: [250, 400],
        mid: [450, 650],
        high: [700, 900]
      }
    };

    // Clientes por segmento (diferenciamos usando percentiles por tier)
    const clientsRanges = {
      chica: [50, 300],
      mediana: [200, 1200],
      grande: [800, 5000]
    };

    // Rating por tier
    const ratingRanges = {
      low: [350, 520],
      mid: [520, 680],
      high: [680, 830]
    };

    // Compras/ventas ratio (margen de contribución inversa) – menor ratio implica más eficiencia
    const comprasRatioRanges = {
      low: [0.62, 0.75],
      mid: [0.50, 0.62],
      high: [0.42, 0.55]
    };

    // Pct ingresos vs meta (valor que mostramos) – luego derivamos meta
    const ingresosPctRanges = {
      low: [0.45, 0.65],   // lejos de la meta
      mid: [0.70, 0.90],   // encaminado
      high: [1.08, 1.25]   // supera meta
    };

    // Pct clientes vs meta – similar lógica
    const clientesPctRanges = {
      low: [0.50, 0.70],
      mid: [0.72, 0.90],
      high: [1.05, 1.20]
    };

    // Factor para ventas año anterior (para crear delta clara)
    const prevYearFactorRanges = {
      low: [1.03, 1.10],   // año anterior mayor -> caída
      mid: [0.92, 0.99],   // crecimiento leve
      high: [0.78, 0.90]   // fuerte crecimiento
    };

    // RNG determinista por id
    const rng = (seed) => {
      let v = (seed * 9301 + 49297) % 233280;
      return () => (v = (v * 9301 + 49297) % 233280) / 233280;
    };
    const rnd = rng(companyId);

    const pickInRange = ([a,b]) => a + rnd() * (b - a);

    // Evolución mensual
    const [mmMin, mmMax] = monthlyRanges[seg][tier];
    const evolucionMM = Array.from({ length: 12 }, () => pickInRange([mmMin, mmMax]));
    const toPeso = (mm) => Math.round(mm * 1_000_000);
    const evolucion = evolucionMM.map(toPeso);
    const ventasAcumuladas = evolucion.reduce((a, b) => a + b, 0);

    // Rating
    const [rMin, rMax] = ratingRanges[tier];
    const ratingCrediticio = Math.round(pickInRange([rMin, rMax]));

    // Compras vs ventas ratio
    const [crMin, crMax] = comprasRatioRanges[tier];
    const comprasRatio = pickInRange([crMin, crMax]);
    const compras = Math.round(ventasAcumuladas * comprasRatio);
    const comprasVsVentas = {
      compras,
      ventas: ventasAcumuladas,
      ratio: Number((compras / ventasAcumuladas).toFixed(2))
    };

    // Ingresos vs Meta
    const ingresosPct = pickInRange(ingresosPctRanges[tier]);
    // Derivar meta a partir del pct mostrado
    const metaIngresosAnual = Math.round(ventasAcumuladas / ingresosPct);
    const pctIngresosVsMeta = Number(ingresosPct.toFixed(2));

    // Clientes vs Meta
    const [cMin, cMax] = clientsRanges[seg];
    // Sesgar distribución según tier (low cerca de cMin, high cerca de cMax)
    const bias = tier === 'low' ? 0.25 : tier === 'mid' ? 0.55 : 0.85;
    const clientesActuales = Math.round(cMin + bias * (cMax - cMin) * (0.85 + rnd()*0.3));
    const clientesPct = pickInRange(clientesPctRanges[tier]);
    const metaClientes = Math.max(clientesActuales, Math.round(clientesActuales / clientesPct));
    const pctClientesVsMeta = Number(clientesPct.toFixed(2));

    // Ventas año anterior
    const [pfMin, pfMax] = prevYearFactorRanges[tier];
    const prevFactor = pickInRange([pfMin, pfMax]);
    const ventasAnioAnterior = Math.round(ventasAcumuladas * prevFactor);

    return {
      empresa: company?.nombre,
      segmento: seg,
      tier,
      ratingCrediticio,
      ventasAcumuladas,
      ventasAnioAnterior,
      comprasVsVentas,
      evolucionVentas: evolucion,
      metaIngresosAnual,
      pctIngresosVsMeta,
      clientesActuales,
      metaClientes,
      pctClientesVsMeta,
      generadoEn: new Date().toISOString(),
    };
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Cargar ejemplo estándar al iniciar
  useEffect(() => {
    const initial = makeSynthetic(companies[0].id);
    setResult(initial);
    setResultRaw(JSON.stringify(initial, null, 2));
    setResultInfo({ contentType: 'application/json', target: 'synthetic-generator' });
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-white to-sky-50 text-slate-900 pt-16">
      {/* Nav */}
  <header className={`fixed top-0 left-0 right-0 z-50 bg-sky-50/80 backdrop-blur border-b border-sky-200 transition-shadow ${scrolled ? "shadow-sm" : "shadow-none"}`}>
  <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div
            className="flex items-center gap-2 cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2 rounded-lg"
            role="button"
            tabIndex={0}
            aria-label="Ir al inicio"
            onClick={() => {
              try {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              } catch {}
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                try {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } catch {}
              }
            }}
          >
            <div className="w-8 h-8 rounded-xl bg-sky-600 text-white grid place-items-center font-semibold">F</div>
            <span className="font-semibold">Fintegram</span>
          </div>
          <nav className="hidden md:flex items-center gap-4 text-sm">
            <Button asChild variant="ghost" aria-label="Ir a Educación" className="focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2 focus-visible:outline-none">
              <a href="https://fintegram.com/education" target="_blank" rel="noopener noreferrer">Educación</a>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  aria-label="Abrir menú de partners"
                  className="focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2 focus-visible:outline-none"
                >
                  Partners
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={6}>
                <DropdownMenuItem asChild>
                  <a href="https://periodicity.ai/" target="_blank" rel="noopener noreferrer">PeriodicityAI</a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href="https://www.tradesilike.com/" target="_blank" rel="noopener noreferrer">TradesILike</a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              onClick={() => setOpenRegister(true)}
              className="rounded-xl bg-gradient-to-r from-sky-600 to-blue-600 text-white hover:from-sky-700 hover:to-blue-700 focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2 focus-visible:outline-none"
            >
              Regístrate
            </Button>
          </nav>
          <div className="flex items-center gap-2 md:hidden">
            <Button asChild variant="ghost" aria-label="Ir a Educación" className="focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2 focus-visible:outline-none">
              <a href="https://fintegram.com/education" target="_blank" rel="noopener noreferrer">Educación</a>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  aria-label="Abrir menú de partners"
                  className="focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2 focus-visible:outline-none"
                >
                  Partners
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={6}>
                <DropdownMenuItem asChild>
                  <a href="https://periodicity.ai/" target="_blank" rel="noopener noreferrer">PeriodicityAI</a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href="https://www.tradesilike.com/" target="_blank" rel="noopener noreferrer">TradesILike</a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              onClick={() => setOpenRegister(true)}
              className="rounded-xl bg-gradient-to-r from-sky-600 to-blue-600 text-white hover:from-sky-700 hover:to-blue-700 focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2 focus-visible:outline-none"
            >
              Regístrate
            </Button>
          </div>
        </div>
      </header>

      <main id="main">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4">
          <BackgroundFX />
          <div className="grid md:grid-cols-2 gap-10 items-start py-12 md:py-20">
            <div>
              <div className="mb-4">
                <Badge>Para PYMEs Chilenas</Badge>
              </div>
              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-3xl md:text-5xl font-semibold leading-tight"
              >
                Somos tu aliado estratégico para mejorar tu acceso a financiamiento bancario
              </motion.h1>
              <p className="mt-4 text-base md:text-lg text-gray-600">
                Carga F29/F22 y obtén un dashboard claro de ingresos/egresos, IVA y un rating crediticio 350–850 con explicación de factores.
              </p>

              {/* Selector de empresas y botón Analizar */}
              <div className="mt-6">
                <div className="text-sm text-muted-foreground mb-2">Selecciona una empresa por clasificación:</div>
                {(['chica','mediana','grande']).map((seg) => (
                  <div key={seg} className="mb-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Empresas {seg === 'chica' ? 'pequeñas' : `${seg}s`}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {companies.filter(c => c.segmento === seg).map((c) => (
                        <label key={c.id} className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer ${selectedCompany === c.id ? 'border-sky-400 bg-sky-50/60' : 'hover:bg-slate-50'}`}>
                          <input
                            type="radio"
                            name="empresa"
                            value={c.id}
                            checked={selectedCompany === c.id}
                            onChange={() => setSelectedCompany(c.id)}
                          />
                          <span className="text-sm">{c.nombre}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="mt-4 flex gap-3">
                  <Button
                    type="button"
                    onClick={async () => {
                      // Inicia flujo de "análisis" sintético
                      setUploadError("");
                      setIsAnalyzing(true);
                      setAnalyzeSteps(["Analizando documentos…", "Extrayendo métricas tributarias…"]);
                      // Mantener resultados actuales visibles mientras corre el análisis

                      // Simular progreso
                      await new Promise((r) => setTimeout(r, 800));
                      setAnalyzeSteps((s) => [...s, "Calculando rating crediticio…"]);
                      await new Promise((r) => setTimeout(r, 900));
                      setAnalyzeSteps((s) => [...s, "Generando visualizaciones…"]);
                      await new Promise((r) => setTimeout(r, 700));

                      // Generar datos sintéticos para la empresa seleccionada
                      const synthetic = makeSynthetic(selectedCompany);
                      setResult(synthetic);
                      setResultRaw(JSON.stringify(synthetic, null, 2));
                      setResultInfo({ contentType: 'application/json', target: 'synthetic-generator' });
                      setIsAnalyzing(false);
                      setAnalyzeSteps((s) => [...s, "Análisis completado."]);
                    }}
                    className="rounded-2xl h-12 px-6 bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700 text-white"
                  >
                    Analizar
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>

                {/* Mensajes de estado del análisis (se muestran en el cuadro de resumen a la derecha) */}
              </div>

              <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                <CheckCircle2 className="w-4 h-4" /> Sin tarjeta de crédito
                <span className="mx-1">•</span>
                <CheckCircle2 className="w-4 h-4" /> Demo guiada disponible
              </div>
              {uploadError && (
                <div className="mt-3 text-sm text-red-600">{uploadError}</div>
              )}
            </div>

            {/* Mock gráfico / visual */}
            <div>
              <Card className="rounded-3xl shadow-xl">
                <CardHeader>
                  <CardTitle>Resumen tributario y Rating Crediticio</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <div className="grid grid-cols-2 gap-4">
                    <div
                      className="rounded-2xl border p-4 cursor-pointer hover:bg-slate-50 focus-within:ring-2 focus-within:ring-sky-600"
                      role="button"
                      tabIndex={0}
                      onClick={() => setDetailKey('rating')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailKey('rating'); } }}
                    >
                      <div className="text-sm text-muted-foreground">Rating Crediticio</div>
                      <div className="flex items-center gap-4 mt-1">
                        <div className="text-4xl font-semibold">{result?.ratingCrediticio ?? 742}</div>
                        {/* Velocímetro */}
                        <div className="relative w-48 h-24 md:w-64 md:h-32" aria-label="Velocímetro rating crediticio">
                          {(() => {
                            const min = 350;
                            const max = 850;
                            const val = Math.max(min, Math.min(max, result?.ratingCrediticio ?? 742));
                            const pct = (val - min) / (max - min); // 0..1
                            // Geometría: usamos un viewBox más grande virtualmente (200x120) con centro (100,100)
                            const cx = 100, cy = 100, r = 85;
                            // Mapeo: 350 -> 180° (izquierda), 850 -> 0° (derecha)
                            const degFor = (v) => 180 - 180 * ((v - min) / (max - min));
                            // Coordenadas en semicírculo superior (y hacia arriba): usar "- sin()" para dibujar arriba del eje horizontal
                            const toXY = (deg, rad = r) => [cx + rad * Math.cos((Math.PI / 180) * deg), cy - rad * Math.sin((Math.PI / 180) * deg)];
                            const arcPath = (a1, a2, rad = r, stroke = '#e2e8f0', width = 6) => {
                              const [x1, y1] = toXY(a1, rad);
                              const [x2, y2] = toXY(a2, rad);
                              const largeArc = Math.abs(a2 - a1) > 180 ? 1 : 0;
                              return <path d={`M${x1} ${y1} A${rad} ${rad} 0 ${largeArc} ${a2 < a1 ? 1 : 0} ${x2} ${y2}`} fill="none" stroke={stroke} strokeWidth={width} strokeLinecap="round" />;
                            };
                            const currentDeg = degFor(val);
                            const ticks = [350, 450, 550, 650, 750, 850];
                            return (
                              <svg viewBox="0 0 200 120" className="w-full h-full">
                                {/* Arco base 180° */}
                                {arcPath(180, 0, r, '#e2e8f0', 8)}
                                {/* Segmentos coloreados como en referencia */}
                                {arcPath(degFor(350), degFor(500), r, '#ef4444', 7)}
                                {arcPath(degFor(500), degFor(650), r, '#f59e0b', 7)}
                                {arcPath(degFor(650), degFor(850), r, '#10b981', 7)}
                                {/* Aguja con animación suave */}
                                <g style={{ transition: 'transform 700ms cubic-bezier(0.22, 1, 0.36, 1)' }} transform={`rotate(${-currentDeg} ${cx} ${cy})`}>
                                  {/* Línea base hacia la derecha desde el centro */}
                                  <line x1={cx} y1={cy} x2={cx + r - 10} y2={cy} stroke="#0f172a" strokeWidth="4" strokeLinecap="round" />
                                  <circle cx={cx} cy={cy} r="6" fill="#0f172a" stroke="white" strokeWidth="1" />
                                </g>
                                {/* Ticks y etiquetas numéricas */}
                                {ticks.map((t, i) => {
                                  const d = degFor(t);
                                  const [ix, iy] = toXY(d, r - 8);
                                  const [ox, oy] = toXY(d, r + 4);
                                  const [lx, ly] = toXY(d, r - 18);
                                  return (
                                    <g key={i}>
                                      <line x1={ix} y1={iy} x2={ox} y2={oy} stroke="#334155" strokeWidth="2" />
                                      <text x={lx} y={ly} fontSize="12" textAnchor="middle" alignmentBaseline="middle" fill="#475569">{t}</text>
                                    </g>
                                  );
                                })}
                              </svg>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {(() => {
                          const s = Number(result?.ratingCrediticio ?? 742);
                          if (s >= 700) return "Riesgo bajo";
                          if (s >= 500) return "Riesgo medio";
                          return "Riesgo alto";
                        })()}
                      </div>
                    </div>
                    <div
                      className="rounded-2xl border p-4 cursor-pointer hover:bg-slate-50 focus-within:ring-2 focus-within:ring-sky-600"
                      role="button"
                      tabIndex={0}
                      onClick={() => setDetailKey('ventas')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailKey('ventas'); } }}
                    >
                      <div className="text-sm text-muted-foreground">Ventas acumuladas</div>
                      <div className="text-2xl font-semibold">{result ? new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(result.ventasAcumuladas) : "$1.8M"}</div>
                      {typeof (result?.ventasAnioAnterior) === 'number' && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Año anterior: {new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(result.ventasAnioAnterior)}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground mt-1">últimos 12 meses</div>
                      {(() => {
                        const curr = result?.ventasAcumuladas;
                        const prev = result?.ventasAnioAnterior;
                        if (typeof curr === 'number' && typeof prev === 'number' && prev > 0) {
                          const isUp = curr >= prev;
                          const deltaPct = ((curr - prev) / prev) * 100;
                          const DeltaIcon = isUp ? TrendingUp : TrendingDown;
                          const color = isUp ? 'text-emerald-600' : 'text-red-600';
                          return (
                            <div className={`mt-2 flex items-center gap-1 ${color}`}>
                              <DeltaIcon className="w-3.5 h-3.5" />
                              <span className="text-xs">{`${isUp ? '+' : ''}${deltaPct.toFixed(1)}%`} vs año anterior</span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    {/* Indicador: Ingresos vs Meta */}
                    <div
                      className="rounded-2xl border p-4 cursor-pointer hover:bg-slate-50 focus-within:ring-2 focus-within:ring-sky-600"
                      role="button"
                      tabIndex={0}
                      onClick={() => setDetailKey('ingresos')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailKey('ingresos'); } }}
                    >
                      <div className="text-sm text-muted-foreground">Ingresos vs Meta</div>
                      {(() => {
                        const pct = Math.round(((result?.pctIngresosVsMeta ?? 0.82) * 100));
                        const pctClamped = Math.max(0, Math.min(100, pct));
                        const meta = result?.metaIngresosAnual;
                        const barColor = pct >= 100 ? 'bg-emerald-500' : pct >= 70 ? 'bg-sky-500' : 'bg-amber-500';
                        return (
                          <>
                            <div className="text-2xl font-semibold">{pct}%</div>
                            <div className="mt-2 h-2 w-full rounded-full bg-slate-200 overflow-hidden">
                              <div className={`${barColor} h-full`} style={{ width: `${pctClamped}%` }} />
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              Meta {meta ? new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(meta) : '—'}
                            </div>
                          </>
                        );
                      })()}
                    </div>

                    {/* Indicador: Clientes vs Meta */}
                    <div
                      className="rounded-2xl border p-4 cursor-pointer hover:bg-slate-50 focus-within:ring-2 focus-within:ring-sky-600"
                      role="button"
                      tabIndex={0}
                      onClick={() => setDetailKey('clientes')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailKey('clientes'); } }}
                    >
                      <div className="text-sm text-muted-foreground">Clientes vs Meta</div>
                      {(() => {
                        const pct = Math.round(((result?.pctClientesVsMeta ?? 0.76) * 100));
                        const pctClamped = Math.max(0, Math.min(100, pct));
                        const actual = result?.clientesActuales ?? 120;
                        const meta = result?.metaClientes ?? 160;
                        const barColor = pct >= 100 ? 'bg-emerald-500' : pct >= 70 ? 'bg-sky-500' : 'bg-amber-500';
                        return (
                          <>
                            <div className="text-2xl font-semibold">{pct}%</div>
                            <div className="mt-2 h-2 w-full rounded-full bg-slate-200 overflow-hidden">
                              <div className={`${barColor} h-full`} style={{ width: `${pctClamped}%` }} />
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              Actual {actual} / Meta {meta}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    <div
                      className="col-span-2 rounded-2xl border p-4 cursor-pointer hover:bg-slate-50 focus-within:ring-2 focus-within:ring-sky-600"
                      role="button"
                      tabIndex={0}
                      onClick={() => setDetailKey('evolucion')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailKey('evolucion'); } }}
                    >
                      <div className="text-sm text-muted-foreground mb-1">Evolución de ventas (MM)</div>
                      {(() => {
                        const data = (result?.evolucionVentas) ?? Array.from({ length: 12 }, () => 10);
                        const max = Math.max(...(data.length ? data : [1]));
                        const monthNames = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
                        const now = new Date();
                        const labels = Array.from({ length: data.length }, (_, idx) => monthNames[(now.getMonth() - (data.length - 1 - idx) + 12) % 12]);
                        return (
                          <>
                            <div className="h-28 flex items-end gap-1">
                              {data.map((v, i) => {
                                const heightPct = Math.max(4, Math.round((v / (max || 1)) * 100));
                                const mm = v / 1_000_000;
                                const label = labels[i];
                                const tooltip = `${label}: ${mm.toLocaleString('es-CL', { maximumFractionDigits: 1, minimumFractionDigits: 0 })} MM`;
                                const labelValue = mm.toLocaleString('es-CL', { maximumFractionDigits: 1, minimumFractionDigits: 0 });
                                return (
                                  <div key={i} className="flex-1 flex flex-col justify-end items-center h-full">
                                    <div
                                      className="relative w-full bg-sky-500 rounded-t"
                                      style={{ height: `${heightPct}%`, minHeight: '12px' }}
                                      title={tooltip}
                                    >
                                      <span className="absolute left-1/2 -translate-x-1/2 top-1 text-white text-[10px] font-medium leading-none drop-shadow select-none pointer-events-none">
                                        {labelValue}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="mt-1 flex items-start gap-1">
                              {labels.map((m, i) => (
                                <div key={i} className="flex-1 text-[10px] leading-3 text-muted-foreground text-center select-none">{m}</div>
                              ))}
                            </div>
                          </>
                        );
                      })()}
                      <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                        {result && result.comprasVsVentas && (
                          <div>
                            Compras vs Ventas: {new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(result.comprasVsVentas.compras)} / {new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(result.comprasVsVentas.ventas)} ({(result.comprasVsVentas.ratio * 100).toFixed(0)}%)
                          </div>
                        )}
                        {result && (
                          <div>
                            Ingresos vs Meta: {(result.pctIngresosVsMeta * 100).toFixed(0)}% (Meta {new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(result.metaIngresosAnual)})
                          </div>
                        )}
                        {result && (
                          <div>
                            Clientes vs Meta: {(result.pctClientesVsMeta * 100).toFixed(0)}% (Actual {result.clientesActuales} / Meta {result.metaClientes})
                          </div>
                        )}
                      </div>
                    </div>
                    </div>
                  {/* Overlay de análisis */}
                  {isAnalyzing && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="absolute inset-0 z-10 bg-white/70 backdrop-blur-sm flex items-center justify-center"
                    >
                      <div className="rounded-2xl border bg-white/90 shadow-xl p-4 w-[92%] max-w-2xl">
                        <div className="flex items-center gap-2 text-slate-700 mb-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm font-medium">Analizando empresa…</span>
                        </div>
                        <div className="h-1 w-full overflow-hidden rounded-full bg-slate-200 mb-3">
                          <div className="h-full w-1/3 bg-gradient-to-r from-sky-500 to-blue-600 animate-[progress_1.2s_ease-in-out_infinite]" style={{
                            animationName: 'progress',
                            animationTimingFunction: 'ease-in-out',
                            animationDuration: '1.2s',
                            animationIterationCount: 'infinite'
                          }} />
                        </div>
                        <ul className="space-y-1 text-sm">
                          {analyzeSteps.map((t, i) => {
                            const done = analyzeSteps.length > 1 && i < analyzeSteps.length - 1;
                            return (
                              <li key={i} className="flex items-center gap-2 text-slate-700">
                                {done ? (
                                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                ) : (
                                  <span className="w-2 h-2 rounded-full bg-sky-600 animate-pulse" />
                                )}
                                <span className={`text-[13px] ${done ? 'text-slate-500 line-through' : ''}`}>{t}</span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </div>
                {/* Se ocultan paneles de respuesta y JSON para vista limpia */}
                <div className="mt-4 flex justify-end">
                  <Button type="button" onClick={() => setOpenReport(true)} className="rounded-2xl focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2 focus-visible:outline-none">Ver más</Button>
                </div>
              </CardContent>
              </Card>
              {/* Modal Reporte PDF (siempre el mismo) */}
              <Dialog open={openReport} onOpenChange={setOpenReport}>
                <DialogContent className="max-w-5xl w-[95vw] h-[88vh] p-0 gap-0 overflow-hidden grid-rows-[auto_1fr]">
                  <DialogHeader className="px-2 py-1 sm:flex sm:items-center sm:justify-between">
                    <DialogTitle className="text-sm">Reporte (PDF)</DialogTitle>
                    <div className="mt-2 sm:mt-0 flex items-center gap-2">
                      <Button asChild variant="secondary" size="sm">
                        <a href={reportUrl} download target="_blank" rel="noopener noreferrer">
                          <Download className="w-4 h-4" /> Descargar
                        </a>
                      </Button>
                      <Button size="sm" onClick={handlePrint}>
                        <Printer className="w-4 h-4" /> Imprimir
                      </Button>
                    </div>
                  </DialogHeader>
                  <div className="relative w-full h-full min-h-0">
                    {pdfOk ? (
                      <iframe
                        ref={iframeRef}
                        title="Reporte estándar"
                        src={reportUrl}
                        className="absolute inset-0 w-full h-full"
                      />
                    ) : (
                      <div className="absolute inset-0 grid place-items-center p-6 text-center text-sm text-slate-600">
                        <div>
                          <div className="text-slate-800 font-medium mb-2">No se pudo cargar el PDF</div>
                          <p>Asegúrate de colocar el archivo en:</p>
                          <div className="font-mono text-xs mt-2">frontend/public/reportes/reporte-estandar.pdf</div>
                          <p className="mt-2">Luego vuelve a recargar la página.</p>
                          <div className="mt-3">
                            <a className="text-sky-700 underline" href={reportUrl} target="_blank" rel="noreferrer">Abrir en nueva pestaña</a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
              {/* Modal Registro simple */
              }
              <Dialog open={openRegister} onOpenChange={setOpenRegister}>
                <DialogContent className="max-w-md w-[92vw]">
                  <DialogHeader>
                    <DialogTitle>Regístrate</DialogTitle>
                  </DialogHeader>
                  <form
                    className="space-y-3"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const errs = {};
                      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regCorreo);
                      const telOk = (regTelefono || "").replace(/[^0-9]/g, "").length >= 8;
                      if (!regNombre.trim()) errs.nombre = "Requerido";
                      if (!regApellido.trim()) errs.apellido = "Requerido";
                      if (!telOk) errs.telefono = "Ingresa un teléfono válido";
                      if (!emailOk) errs.correo = "Correo inválido";
                      setRegErrors(errs);
                      if (Object.keys(errs).length === 0) {
                        (async () => {
                          try {
                            setRegLoading(true);
                            const res = await fetch('/api/register', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                nombre: regNombre,
                                apellido: regApellido,
                                telefono: regTelefono,
                                correo: regCorreo,
                                goal,
                                amountRange,
                                empresa: actionResult?.empresa || result?.empresa || companies.find(c=>c.id===selectedCompany)?.nombre,
                                rating: actionResult?.ratingCrediticio || result?.ratingCrediticio,
                              }),
                            });
                            const data = await res.json().catch(() => ({}));
                            if (!res.ok || !data?.ok) {
                              throw new Error(data?.error || 'No se pudo registrar, intenta nuevamente.');
                            }
                            toast.success('¡Gracias! Te contactaremos pronto.');
                            setOpenRegister(false);
                            setRegNombre("");
                            setRegApellido("");
                            setRegTelefono("");
                            setRegCorreo("");
                          } catch (err) {
                            toast.error(String(err?.message || err));
                          } finally {
                            setRegLoading(false);
                          }
                        })();
                      }
                    }}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="reg-nombre">Nombre</Label>
                        <Input id="reg-nombre" value={regNombre} onChange={(e) => setRegNombre(e.target.value)} placeholder="Tu nombre" required />
                        {regErrors.nombre && <p className="mt-1 text-xs text-red-600">{regErrors.nombre}</p>}
                      </div>
                      <div>
                        <Label htmlFor="reg-apellido">Apellido</Label>
                        <Input id="reg-apellido" value={regApellido} onChange={(e) => setRegApellido(e.target.value)} placeholder="Tu apellido" required />
                        {regErrors.apellido && <p className="mt-1 text-xs text-red-600">{regErrors.apellido}</p>}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="reg-telefono">Teléfono</Label>
                      <Input id="reg-telefono" value={regTelefono} onChange={(e) => setRegTelefono(e.target.value)} placeholder="Ej: 9 1234 5678" inputMode="tel" required />
                      {regErrors.telefono && <p className="mt-1 text-xs text-red-600">{regErrors.telefono}</p>}
                    </div>
                    <div>
                      <Label htmlFor="reg-correo">Correo</Label>
                      <Input id="reg-correo" value={regCorreo} onChange={(e) => setRegCorreo(e.target.value)} placeholder="tucorreo@ejemplo.com" type="email" required />
                      {regErrors.correo && <p className="mt-1 text-xs text-red-600">{regErrors.correo}</p>}
                    </div>
                    <div className="pt-1 flex items-center justify-end gap-2">
                      <Button type="button" variant="secondary" onClick={() => setOpenRegister(false)} disabled={regLoading}>Cancelar</Button>
                      <Button type="submit" className="bg-sky-600 hover:bg-sky-700 text-white" disabled={regLoading}>{regLoading ? 'Enviando…' : 'Enviar'}</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Modal de detalle explicativo de KPIs */}
              <Dialog open={!!detailKey} onOpenChange={(open) => { if (!open) setDetailKey(null); }}>
                <DialogContent className="max-w-lg w-[92vw]">
                  <DialogHeader>
                    <DialogTitle>
                      {detailKey === 'rating' && 'Rating Crediticio'}
                      {detailKey === 'ventas' && 'Ventas acumuladas (12 meses)'}
                      {detailKey === 'ingresos' && 'Ingresos vs Meta'}
                      {detailKey === 'clientes' && 'Clientes vs Meta'}
                      {detailKey === 'evolucion' && 'Evolución de ventas (MM)'}
                    </DialogTitle>
                  </DialogHeader>
                  {(() => {
                    const fmt = (v) => typeof v === 'number' ? new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(v) : '—';
                    const r = result || {};
                    const rating = Number(r.ratingCrediticio || 0);
                    const tramo = rating >= 700 ? 'bajo riesgo' : rating >= 500 ? 'riesgo medio' : 'alto riesgo';
                    const curr = r.ventasAcumuladas;
                    const prev = r.ventasAnioAnterior;
                    const deltaOK = typeof curr === 'number' && typeof prev === 'number' && prev > 0;
                    const deltaPct = deltaOK ? (((curr - prev) / prev) * 100) : null;
                    const ingresosPct = r.pctIngresosVsMeta ? Math.round(r.pctIngresosVsMeta * 100) : null;
                    const clientesPct = r.pctClientesVsMeta ? Math.round(r.pctClientesVsMeta * 100) : null;
                    const clientesActuales = r.clientesActuales;
                    const metaClientes = r.metaClientes;
                    const metaIngresos = r.metaIngresosAnual;
                    const evo = Array.isArray(r.evolucionVentas) ? r.evolucionVentas : [];
                    const monthNames = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
                    const now = new Date();
                    const labels = evo.length ? Array.from({ length: evo.length }, (_, idx) => monthNames[(now.getMonth() - (evo.length - 1 - idx) + 12) % 12]) : [];
                    const maxIdx = evo.length ? evo.indexOf(Math.max(...evo)) : -1;
                    const minIdx = evo.length ? evo.indexOf(Math.min(...evo)) : -1;

                    if (detailKey === 'rating') {
                      return (
                        <div className="text-sm text-slate-700 space-y-2">
                          <div className="text-base font-medium">Tu rating: {rating || '—'} (escala 350–850)</div>
                          <p>
                            Es un indicador que estima tu probabilidad de pago. Mientras más alto, mejor acceso y condiciones de crédito.
                          </p>
                          <ul className="list-disc pl-5 space-y-1">
                            <li>Tramos de referencia: 350–499 alto riesgo, 500–699 riesgo medio, 700–850 bajo riesgo.</li>
                            <li>Tu empresa hoy se encuentra en <span className="font-medium">{tramo}</span>.</li>
                          </ul>
                          <p className="text-slate-600">Este valor se calcula con tu desempeño reciente (ventas, estabilidad y eficiencia) para dar una señal clara a bancos y proveedores.</p>
                        </div>
                      );
                    }

                    if (detailKey === 'ventas') {
                      return (
                        <div className="text-sm text-slate-700 space-y-2">
                          <div className="text-base font-medium">Ventas acumuladas: {fmt(curr)}</div>
                          {deltaOK && (
                            <div className={deltaPct >= 0 ? 'text-emerald-700' : 'text-red-700'}>
                              Variación vs. año anterior: {deltaPct >= 0 ? '+' : ''}{deltaPct?.toFixed(1)}%
                            </div>
                          )}
                          <p>
                            Es la suma de tus ventas de los últimos 12 meses. Te ayuda a dimensionar el tamaño de tu negocio y tu capacidad de pago.
                          </p>
                          <p className="text-slate-600">Un crecimiento sostenido mejora tus alternativas de financiamiento; una caída invita a revisar precios, mix de productos y eficiencia.</p>
                        </div>
                      );
                    }

                    if (detailKey === 'ingresos') {
                      const faltante = (typeof metaIngresos === 'number' && typeof curr === 'number') ? Math.max(0, metaIngresos - curr) : null;
                      return (
                        <div className="text-sm text-slate-700 space-y-2">
                          <div className="text-base font-medium">Avance: {ingresosPct != null ? `${ingresosPct}%` : '—'}</div>
                          <div>Meta anual: {fmt(metaIngresos)}</div>
                          <div>Ventas actuales: {fmt(curr)}{faltante != null && ` · Restante: ${fmt(faltante)}`}</div>
                          <p>
                            Compara tus ingresos actuales con la meta anual definida. Te muestra qué tan encaminado vas y cuánto falta por lograr.
                          </p>
                          <p className="text-slate-600">Al superar el 100% estás sobrecumpliendo tu objetivo; bajo 70% conviene ajustar acciones comerciales y control de costos.</p>
                        </div>
                      );
                    }

                    if (detailKey === 'clientes') {
                      return (
                        <div className="text-sm text-slate-700 space-y-2">
                          <div className="text-base font-medium">Avance: {clientesPct != null ? `${clientesPct}%` : '—'}</div>
                          <div>Clientes actuales: {typeof clientesActuales === 'number' ? clientesActuales : '—'} · Meta: {typeof metaClientes === 'number' ? metaClientes : '—'}</div>
                          <p>
                            Mide tu progreso en cantidad de clientes respecto de una meta. Más clientes estables diversifican el riesgo y fortalecen el flujo de caja.
                          </p>
                          <p className="text-slate-600">Si el avance es bajo, considera activar referidos, campañas puntuales y mejoras en retención.</p>
                        </div>
                      );
                    }

                    // evolucion
                    return (
                      <div className="text-sm text-slate-700 space-y-2">
                        <p>
                          Muestra tus ventas mes a mes en millones. Te ayuda a detectar estacionalidad y meses fuertes/débiles.
                        </p>
                        {maxIdx >= 0 && (
                          <div>Mes más alto: <span className="font-medium">{labels[maxIdx]} {fmt(evo[maxIdx])}</span></div>
                        )}
                        {minIdx >= 0 && (
                          <div>Mes más bajo: <span className="font-medium">{labels[minIdx]} {fmt(evo[minIdx])}</span></div>
                        )}
                        <p className="text-slate-600">Usa esta lectura para planificar stock, caja y campañas en los meses clave.</p>
                      </div>
                    );
                  })()}
                </DialogContent>
              </Dialog>
              {/* Callout alineado al cuadro de resumen */}
              <div className="mt-4">
                <div className="rounded-3xl border border-sky-200 bg-gradient-to-r from-sky-50 to-white p-6 md:p-8">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-sky-700 mt-1" />
                    <div>
                      <p className="text-slate-700 text-lg md:text-xl font-medium">
                        Democratizar el acceso al crédito comienza por entender los datos…
                      </p>
                      <p className="text-slate-900 text-2xl md:text-3xl font-semibold mt-1">Con Fintegram</p>
                      <p className="text-slate-700 text-base md:text-lg mt-1">Valida tu negocio. Escala tu impacto.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Valor */}
      <section id="valor" className="relative py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-4">
          {/* Acción: explicación y selección de objetivo */}
          <div className="mt-6 md:mt-8 mb-16 md:mb-24 rounded-3xl border bg-white p-5 md:p-7 shadow-sm">
            <div className="grid md:grid-cols-2 gap-6 items-center">
              <div>
                <h3 className="text-xl md:text-2xl font-semibold">¿Quieres mejorar tu rating? Así funciona</h3>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 w-4 h-4 text-sky-700"/>Elige un objetivo.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 w-4 h-4 text-sky-700"/>Te evaluamos en el momento.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 w-4 h-4 text-sky-700"/>Recibe una oferta pre aprobada o un plan personalizado según tu radiografía.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 w-4 h-4 text-sky-700"/>Haz seguimiento de tu progreso.</li>
                </ul>
              </div>
              <div>
                {(() => {
                  const goodThreshold = 700;
                  const activeRating = actionResult?.ratingCrediticio ?? result?.ratingCrediticio ?? 0;
                  const isGood = activeRating >= goodThreshold;
                  const goalOptions = [
                    { key: 'capital', label: 'Capital de trabajo', icon: Briefcase },
                    { key: 'linea', label: 'Línea de crédito', icon: CreditCard },
                    { key: 'factoring', label: 'Factoring', icon: FileText },
                    { key: 'seguros', label: 'Seguros', icon: Shield },
                  ];
                  const amountOptions = ['5-10M','10-25M','25-50M','50M+'];

                  const CompanySelector = () => (
                    <div>
                      <div className="text-sm text-slate-600 mb-2">Selecciona la empresa</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {companies.map((c) => (
                          <label key={c.id} className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer ${actionCompanyId === c.id ? 'border-sky-500 bg-sky-50/60' : 'hover:bg-slate-50'}`}>
                            <input
                              type="radio"
                              name="empresa_accion"
                              value={c.id}
                              checked={actionCompanyId === c.id}
                              onChange={() => {
                                setActionCompanyId(c.id);
                              }}
                            />
                            <span className="text-sm">{c.nombre}</span>
                          </label>
                        ))}
                      </div>
                      <div className="mt-3">
                        <Button
                          className="rounded-xl bg-gradient-to-r from-sky-600 to-blue-600 text-white hover:from-sky-700 hover:to-blue-700"
                          onClick={() => {
                            const syn = makeSynthetic(actionCompanyId);
                            setActionResult(syn);
                            // Sincronizar con selección global para que todo el dashboard use esta empresa
                            setSelectedCompany(actionCompanyId);
                            setResult(syn);
                            setResultRaw(JSON.stringify(syn, null, 2));
                            setResultInfo({ contentType: 'application/json', target: 'synthetic-generator' });
                            // Reiniciar selección de objetivo/monto al cambiar de empresa
                            setGoal("");
                            setAmountRange("");
                            setActionStep('goal');
                          }}
                        >
                          Siguiente
                        </Button>
                      </div>
                    </div>
                  );

                  if (actionStep === 'company') {
                    return <CompanySelector />;
                  }

                  if (actionStep === 'goal') {
                    return (
                      <div>
                        <div className="text-sm text-slate-600 mb-2">¿Cuál es tu objetivo hoy?</div>
                        <div className="grid grid-cols-2 gap-2">
                          {goalOptions.map(({ key, label, icon: Icon }) => {
                            const active = goal === key;
                            return (
                              <button
                                key={key}
                                type="button"
                                onClick={() => setGoal(key)}
                                className={`group rounded-xl border p-3 text-left transition-colors ${active ? 'border-sky-500 bg-sky-50/60' : 'hover:bg-slate-50'}`}
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`w-8 h-8 rounded-lg grid place-items-center ${active ? 'bg-sky-600 text-white' : 'bg-gray-100 text-slate-700'}`}>
                                    <Icon className="w-4 h-4" />
                                  </div>
                                  <div className="text-sm font-medium">{label}</div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        <div className="mt-3">
                          <Button
                            className="rounded-xl bg-gradient-to-r from-sky-600 to-blue-600 text-white hover:from-sky-700 hover:to-blue-700"
                            disabled={!goal}
                            onClick={() => setActionStep('amount')}
                          >
                            Siguiente
                          </Button>
                          <Button variant="secondary" className="ml-2" onClick={() => setActionStep('company')}>Volver</Button>
                        </div>
                      </div>
                    );
                  }

                  if (actionStep === 'amount') {
                    return (
                      <div>
                        {actionResult && (
                          <div className="text-xs text-slate-500 mb-1">
                            Empresa: <span className="font-medium text-slate-700">{actionResult.empresa}</span> · Rating: <span className={`font-medium ${isGood ? 'text-emerald-700' : 'text-amber-700'}`}>{actionResult.ratingCrediticio}</span>
                          </div>
                        )}
                        <div className="text-sm text-slate-600 mb-2">¿Qué rango de dinero necesitas?</div>
                        <div className="grid grid-cols-2 gap-2">
                          {amountOptions.map((opt) => {
                            const active = amountRange === opt;
                            return (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => setAmountRange(opt)}
                                className={`rounded-xl border p-3 text-sm font-medium ${active ? 'border-sky-500 bg-sky-50/60' : 'hover:bg-slate-50'}`}
                              >
                                {opt.replace('M', ' millones')}
                              </button>
                            );
                          })}
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <Button variant="secondary" onClick={() => setActionStep('goal')}>Volver</Button>
                          <Button
                            className="rounded-xl bg-gradient-to-r from-sky-600 to-blue-600 text-white hover:from-sky-700 hover:to-blue-700"
                            disabled={!amountRange}
                            onClick={() => setActionStep(isGood ? 'offer' : 'plan')}
                          >
                            Siguiente
                          </Button>
                        </div>
                      </div>
                    );
                  }

                  if (actionStep === 'offer') {
                    const parseRange = (r) => {
                      switch (r) {
                        case '5-10M': return [5_000_000, 10_000_000];
                        case '10-25M': return [10_000_000, 25_000_000];
                        case '25-50M': return [25_000_000, 50_000_000];
                        case '50M+': return [50_000_000, Infinity];
                        default: return [0, Infinity];
                      }
                    };
                    const computePre = (res, range) => {
                      if (!res || !range) return null;
                      const [lo, hi] = parseRange(range);
                      const avgMonthly = (res.ventasAcumuladas || 0) / 12;
                      let mult = 1.5;
                      if (res.ratingCrediticio >= 780) mult = 3.0;
                      else if (res.ratingCrediticio >= 740) mult = 2.5;
                      else if (res.ratingCrediticio >= 700) mult = 2.0;
                      const soft = avgMonthly * mult;
                      let amt = Math.max(lo, Math.min(soft, hi));
                      // Redondeo al 0.5M más cercano
                      amt = Math.round(amt / 500_000) * 500_000;
                      return amt;
                    };
                    const offerAmount = computePre(actionResult || result, amountRange);
                    return (
                      <div>
                        <div className="text-sm text-emerald-700 font-semibold">Oferta pre-aprobada</div>
                        <div className="mt-1 text-sm text-slate-700">Para objetivo <span className="font-medium">{goalLabel(goal)}</span> y monto <span className="font-medium">{amountRange}</span>.</div>
                        <div className="mt-3 rounded-xl border p-3 bg-emerald-50 text-emerald-800 text-sm">
                          {offerAmount ? (
                            <>
                              Oferta pre-aprobada: <span className="font-semibold">{new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(offerAmount)}</span>
                              <div className="text-emerald-900/80 mt-1">Estimada según tu rating {actionResult?.ratingCrediticio ?? result?.ratingCrediticio} y tus ventas.</div>
                            </>
                          ) : (
                            <>Basado en tu calificación actual, cuentas con una oferta pre-aprobada. Te contactaremos para confirmar condiciones.</>
                          )}
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <Button variant="secondary" onClick={() => setActionStep('amount')}>Volver</Button>
                          <Button
                            className="rounded-xl bg-gradient-to-r from-sky-600 to-blue-600 text-white hover:from-sky-700 hover:to-blue-700"
                            onClick={() => setOpenRegister(true)}
                          >
                            Solicitar
                          </Button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div>
                      <div className="text-sm text-slate-700">Tu rating actual no califica para una oferta inmediata. Construyamos un plan para alcanzar tu meta.</div>
                      <div className="mt-3 flex items-center gap-2">
                        <Button variant="secondary" onClick={() => setActionStep('amount')}>Volver</Button>
                        <Button
                          className="rounded-xl bg-gradient-to-r from-sky-600 to-blue-600 text-white hover:from-sky-700 hover:to-blue-700"
                          onClick={() => {
                            toast.info(`Objetivo: ${goalLabel(goal)} | Monto: ${amountRange}`);
                            setOpenRegister(true);
                          }}
                        >
                          Construir mi plan
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
          {/* Proceso paso a paso */}
          <div className="relative">
            {/* Línea de progreso detrás de las tarjetas (desktop) */}
            <div className="hidden md:block absolute top-10 left-0 right-0 h-0.5 bg-gray-200"/>

            <div className="grid md:grid-cols-3 gap-4 md:gap-6 items-stretch">
              {/* Paso 1 */}
              <motion.div className="h-full" whileHover={{ y: -6, rotate: -0.2 }} whileTap={{ scale: 0.995 }} transition={{ type: "spring", stiffness: 260, damping: 18 }}>
                <Card className="h-full relative transition-colors hover:border-sky-200 hover:bg-sky-50/40">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-sky-600 text-white grid place-items-center text-sm font-semibold">1</div>
                      <div className="w-8 h-8 rounded-2xl bg-gray-100 flex items-center justify-center">
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <CardTitle className="text-base md:text-lg">Registro</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground">
                    Conexión a SII y otras fuentes oficiales
                  </CardContent>
                </Card>
              </motion.div>

              {/* Paso 2 */}
              <motion.div className="h-full" whileHover={{ y: -6, rotate: -0.2 }} whileTap={{ scale: 0.995 }} transition={{ type: "spring", stiffness: 260, damping: 18 }}>
                <Card className="h-full relative transition-colors hover:border-sky-200 hover:bg-sky-50/40">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-sky-600 text-white grid place-items-center text-sm font-semibold">2</div>
                      <div className="w-8 h-8 rounded-2xl bg-gray-100 flex items-center justify-center">
                        <BarChart3 className="w-4 h-4" />
                      </div>
                      <CardTitle className="text-base md:text-lg">Radiografía y Scoring</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground">
                    Análisis financiero y calificación crediticia
                  </CardContent>
                </Card>
              </motion.div>

              {/* Paso 3 */}
              <motion.div className="h-full" whileHover={{ y: -6, rotate: -0.2 }} whileTap={{ scale: 0.995 }} transition={{ type: "spring", stiffness: 260, damping: 18 }}>
                <Card className="h-full relative transition-colors hover:border-sky-200 hover:bg-sky-50/40">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-sky-600 text-white grid place-items-center text-sm font-semibold">3</div>
                      <div className="w-8 h-8 rounded-2xl bg-gray-100 flex items-center justify-center">
                        <ShieldCheck className="w-4 h-4" />
                      </div>
                      <CardTitle className="text-base md:text-lg">Consultoría y Acompañamiento</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground">
                    Asesoría para mejorar indicadores de gestión
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Funciones */}
      <section id="funciones" className="relative py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-sky-700 to-blue-600">Lo que obtienes</h2>
            <p className="text-gray-600 mt-2">Resultados accionables, no solo gráficos bonitos.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Card 1 */}
            <motion.div className="h-full" whileHover={{ y: -6 }} whileTap={{ scale: 0.995 }} transition={{ type: "spring", stiffness: 260, damping: 18 }}>
              <Card className="h-full transition-colors hover:border-sky-200 hover:bg-sky-50/40">
                <CardHeader className="pb-2">
                  <div className="h-1 w-12 rounded-full bg-gradient-to-r from-sky-500 to-blue-600 mb-3" />
                  <div className="text-sky-700 text-xs font-semibold tracking-wide uppercase">Cuadro de Mando Financiero</div>
                  <CardTitle className="text-lg md:text-xl">Inteligencia financiera</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                  Visualización simple del estado de salud de tu negocio
                </CardContent>
              </Card>
            </motion.div>

            {/* Card 2 */}
            <motion.div className="h-full" whileHover={{ y: -6 }} whileTap={{ scale: 0.995 }} transition={{ type: "spring", stiffness: 260, damping: 18 }}>
              <Card className="h-full transition-colors hover:border-sky-200 hover:bg-sky-50/40">
                <CardHeader className="pb-2">
                  <div className="h-1 w-12 rounded-full bg-gradient-to-r from-sky-500 to-blue-600 mb-3" />
                  <div className="text-sky-700 text-xs font-semibold tracking-wide uppercase">Asesoramiento Crediticio</div>
                  <CardTitle className="text-lg md:text-xl">Asesoría Personalizada</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                  Análisis crediticio y consejos accionables basados en datos reales, potenciados por IA.
                </CardContent>
              </Card>
            </motion.div>

            {/* Card 3 */}
            <motion.div className="h-full" whileHover={{ y: -6 }} whileTap={{ scale: 0.995 }} transition={{ type: "spring", stiffness: 260, damping: 18 }}>
              <Card className="h-full transition-colors hover:border-sky-200 hover:bg-sky-50/40">
                <CardHeader className="pb-2">
                  <div className="h-1 w-12 rounded-full bg-gradient-to-r from-sky-500 to-blue-600 mb-3" />
                  <div className="text-sky-700 text-xs font-semibold tracking-wide uppercase">Accede a Financiamiento</div>
                  <CardTitle className="text-lg md:text-xl">Planes adaptados a la realidad de tu negocio</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                  Te conectamos con alternativas de crédito alineadas a tu flujo de caja, con plazos y montos sostenibles.
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonios eliminados */}

      

      {/* CTA final */}
      <section className="py-16 text-white bg-gradient-to-br from-sky-700 via-blue-700 to-indigo-800">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h3 className="text-2xl md:text-3xl font-semibold">¿Listo para ver tu rating crediticio ahora?</h3>
          <p className="mt-2 text-white/80">Solicita una demo o empieza gratis. Toma menos de 2 minutos.</p>
          <div className="mt-6 flex items-center justify-center">
            <Button
              size="lg"
              className="rounded-2xl bg-white text-sky-700 hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
              onClick={() => {
                try {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } catch {}
                setTimeout(() => setOpenRegister(true), 350);
              }}
            >
              Solicitar demo
            </Button>
          </div>
        </div>
      </section>

  </main>

  {/* Fondo sutil inferior */}
  <BottomFX />

      {/* Footer */}
      <footer className="py-10 text-sm text-gray-600">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <div>© {new Date().getFullYear()} Fintegram. Todos los derechos reservados.</div>
            <div className="text-xs text-slate-500 max-w-2xl">
              Somos un producto de SW Audit Chile Ltda, firma registrada bajo el Número 54 de la Comisión de Mercado Financiero CMF.
            </div>
          </div>
          <div className="flex items-center gap-4 hidden" aria-hidden="true">
            <a href="#" className="hover:text-gray-900">Términos</a>
            <a href="#" className="hover:text-gray-900">Privacidad</a>
            <a href="#" className="hover:text-gray-900">Contacto</a>
          </div>
        </div>
      </footer>

      <CookieConsentBanner />
    </div>
  );
}
